// Assessment content + scoring. Kept as data so the copy iterates freely and
// the scoring stays predictable and testable.
//
// IMPORTANT: question `key`s and option `value`s are a contract with the
// backend — `_ASSESS_ENUMS` in backend/server.py validates against exactly
// these strings. Change one, change both.

export const assessCopy = {
  intro: {
    eyebrow: 'Diagnostic · 5 minutes',
    headline: 'Find out what your business would actually get from AI.',
    body:
      "Seven questions about how your business really runs. At the end you get a written read on where the opportunity is, which systems fit, and — honestly — whether it is worth doing at all yet. No sign-up. No email needed to see it.",
    startCta: 'Start the diagnostic',
    reassure: [
      'Seven questions, about five minutes',
      'You see the full result without giving us anything',
      'If the answer is “not yet”, that is what it will say',
    ],
  },

  progressLabel: 'Question',

  questions: [
    {
      key: 'business',
      ref: 'Q-01',
      label: 'What kind of business do you run?',
      hint: 'So we can pattern-match against work we have already done.',
      options: [
        { value: 'ecommerce',   label: 'E-commerce / Retail' },
        { value: 'clinic',      label: 'Clinic / Healthcare' },
        { value: 'school',      label: 'School / Education' },
        { value: 'property',    label: 'Property / Real estate' },
        { value: 'services',    label: 'Professional services — agency, consultancy, legal' },
        { value: 'saas',        label: 'SaaS / Software' },
        { value: 'other',       label: 'Something else' },
      ],
    },
    {
      key: 'team',
      ref: 'Q-02',
      label: 'How many people work there?',
      hint: 'Team size changes what is worth automating and what is not.',
      options: [
        { value: 'solo',        label: 'Just me' },
        { value: '2-5',         label: '2 – 5 people' },
        { value: '5-25',        label: '5 – 25' },
        { value: '25-100',      label: '25 – 100' },
        { value: '100-plus',    label: '100+' },
      ],
    },
    {
      key: 'bottleneck',
      ref: 'Q-03',
      label: 'Where does the time actually go?',
      hint: 'Pick the one that hurts most this week — not in theory.',
      options: [
        { value: 'support',     label: 'Answering the same customer questions over and over' },
        { value: 'ops',         label: 'Moving information between systems by hand' },
        { value: 'sales',       label: 'Finding and reaching the right prospects' },
        { value: 'all',         label: 'All three, honestly' },
        { value: 'unsure',      label: 'Not sure — that is part of the problem' },
      ],
    },
    {
      key: 'tried',
      ref: 'Q-04',
      label: 'Have you tried AI for this before?',
      hint: 'Everyone has tried something. The failures are the useful part.',
      options: [
        { value: 'never',             label: 'No — this would be the first attempt' },
        { value: 'chatgpt',           label: 'Yes — ChatGPT or similar, used by hand' },
        { value: 'freelancer-ok',     label: 'Yes — hired a freelancer, mixed results' },
        { value: 'freelancer-failed', label: 'Yes — hired a freelancer, it never shipped' },
        { value: 'agency-failed',     label: 'Yes — hired an agency, never reached production' },
      ],
    },
    {
      key: 'timeline',
      ref: 'Q-05',
      label: 'When would you want this running?',
      hint: null,
      options: [
        { value: 'month',       label: 'Within a month — it is urgent' },
        { value: 'quarter',     label: 'This quarter' },
        { value: 'year',        label: 'Sometime this year' },
        { value: 'exploring',   label: 'No timeline — just seeing what is possible' },
      ],
    },
    {
      key: 'budget',
      ref: 'Q-06',
      label: 'What could you put behind it?',
      hint: 'Ranges only, nothing binding. This changes what we would recommend, not whether we answer.',
      options: [
        { value: 'under-2k',    label: 'Under $2,000' },
        { value: '2k-10k',      label: '$2,000 – $10,000' },
        { value: '10k-50k',     label: '$10,000 – $50,000' },
        { value: '50k-plus',    label: '$50,000+' },
        { value: 'unsure',      label: 'Genuinely do not know yet' },
      ],
    },
    {
      key: 'notes',
      ref: 'Q-07',
      label: 'Anything we should know before we answer?',
      hint: 'Optional. The more specific, the more specific we can be back.',
      type: 'text',
      placeholder:
        'e.g. we tried a chatbot last year and customers hated it — I want to understand why before trying again',
      maxLength: 1000,
    },
  ],

  // Shown while the recommendation is being assembled.
  analysing: [
    'Reading your answers',
    'Matching against work we have delivered',
    'Ranking the opportunity',
    'Checking whether it is worth doing yet',
  ],

  results: {
    call: {
      title: 'Start with a conversation',
      subtitle: 'Free · 30 minutes',
      body:
        "An engagement is not the right first move for you yet. A call will get you further than anything we could sell you at this stage — we will work out together whether there is a real fit before either side commits to anything.",
      cta: 'Book the call',
    },
    audit: {
      title: 'The Audit',
      subtitle: 'Free · 1 – 2 days',
      body:
        "You have real problems to solve but the priorities are not clear yet — and guessing wrong here is expensive. The Audit gives you a ranked map of where AI genuinely fits, what each opportunity is worth, and what it would cost to build. You keep the plan whether or not you build with us.",
      cta: 'Book a call to scope the Audit',
    },
    build: {
      title: 'Go straight to Build',
      subtitle: '6 – 9 days',
      body:
        "You have named the pain and you have something behind it. We can skip the discovery theatre — scope it on a call, price it in writing, and start. We build it, wire it into what you already use, and test it against real cases from your business before a customer ever touches it.",
      cta: 'Book a call to scope the Build',
    },
    rescue: {
      title: 'The Rescue',
      subtitle: '2 days to a verdict',
      body:
        "You already paid for an AI project that never shipped. Before you spend anything else, you need to know why. Two days and we will tell you what actually went wrong, what is salvageable, and what it would honestly cost to finish. Then you get a clean choice — kill it, finish it in-house, or finish it with us.",
      cta: 'Talk to us about the stalled project',
    },
    retainer: {
      title: 'Add a retainer when you build',
      body:
        "At your size, whatever gets built will drift as the business changes. A monthly retainer keeps it accurate, patched, and improving — reliably cheaper than one emergency rebuild a year.",
    },
  },

  // Soft email gate — shown *after* the full result, never before it.
  capture: {
    eyebrow: 'Optional',
    headline: 'Want this as a written brief?',
    body:
      'We will send the full read — matched systems, first move, and what each phase would cost — as a document you can forward to whoever else needs to see it.',
    namePlaceholder: 'Your name',
    emailPlaceholder: 'you@company.com',
    companyPlaceholder: 'Company (optional)',
    cta: 'Send me the brief',
    success: 'Sent. Check your inbox in the next few minutes.',
    error: 'That did not go through. Try again, or just book a call.',
    privacy: 'One email with your brief. No list, no newsletter, no sharing.',
  },
};

// ── The three systems, referenced from the personalised readout ──────────
export const SYSTEMS = {
  support: {
    ref: 'SYS-01',
    name: 'Support that answers instantly',
    line: 'An agent on your site that has read your products and your past tickets.',
  },
  ops: {
    ref: 'SYS-02',
    name: 'Operations that run themselves',
    line: 'The hand-work between your tools, rebuilt as workflows nobody has to run.',
  },
  sales: {
    ref: 'SYS-03',
    name: 'A pipeline that fills itself',
    line: 'Finding, researching, and opening conversations with the right companies.',
  },
};

const BUSINESS_LABEL = {
  ecommerce: 'an online retail business',
  clinic:    'a clinic',
  school:    'a school',
  property:  'a property business',
  services:  'a professional services firm',
  saas:      'a software company',
  other:     'your business',
};

const TEAM_LABEL = {
  solo:       'a one-person operation',
  '2-5':      'a team of two to five',
  '5-25':     'a team under twenty-five',
  '25-100':   'a team of twenty-five to a hundred',
  '100-plus': 'an organisation of a hundred-plus',
};

// Which systems each bottleneck implies, in priority order.
const SYSTEMS_FOR = {
  support: ['support', 'ops'],
  ops:     ['ops', 'support'],
  sales:   ['sales', 'ops'],
  all:     ['support', 'ops', 'sales'],
  unsure:  ['support', 'ops', 'sales'],
};

/*
 * Readiness — an honest 0-100 read on how much a business would get out of
 * building right now. Shown to the respondent, because a low score is
 * genuinely useful information to them, and stored so inbound can be
 * prioritised without a second hidden score.
 */
const READINESS_POINTS = {
  timeline: { month: 30, quarter: 24, year: 12, exploring: 2 },
  budget:   { '50k-plus': 26, '10k-50k': 24, '2k-10k': 16, unsure: 8, 'under-2k': 3 },
  bottleneck: { support: 22, ops: 22, sales: 22, all: 16, unsure: 6 },
  team:     { '100-plus': 14, '25-100': 14, '5-25': 11, '2-5': 7, solo: 3 },
  tried:    {
    'agency-failed': 8, 'freelancer-failed': 8, 'freelancer-ok': 6, chatgpt: 5, never: 4,
  },
};

function readinessScore(a) {
  return Object.entries(READINESS_POINTS).reduce(
    (sum, [field, table]) => sum + (table[a?.[field]] ?? 0),
    0
  );
}

const BANDS = [
  { min: 78, key: 'urgent',    label: 'Ready to build',
    note: 'You have a named problem, a timeline, and something behind it. The only thing left is scoping it properly.' },
  { min: 55, key: 'ready',     label: 'Ready to scope',
    note: 'The opportunity is real. What is missing is a ranked plan so the first build is the right one.' },
  { min: 32, key: 'forming',   label: 'Worth mapping first',
    note: 'There is something here, but building before mapping it would be guessing with your money.' },
  { min: 0,  key: 'exploring', label: 'Not yet — and that is fine',
    note: 'Nothing here needs a build this quarter. A conversation will get you further than an invoice would.' },
];

function band(score) {
  return BANDS.find((b) => score >= b.min) || BANDS[BANDS.length - 1];
}

/*
 * Rules-based recommendation. Runs on both client (instant render) and
 * server (authoritative). The `engagement` branch here MUST stay in sync
 * with `_assess_recommend()` in backend/server.py.
 */
export function recommendEngagement(a) {
  const { bottleneck, tried, timeline, budget, team } = a || {};
  const withRetainer = team === '25-100' || team === '100-plus';

  if (timeline === 'exploring' || budget === 'under-2k') {
    return { engagement: 'call', withRetainer: false };
  }
  if (tried === 'agency-failed' || tried === 'freelancer-failed') {
    return { engagement: 'rescue', withRetainer: false };
  }
  if (bottleneck === 'unsure' || bottleneck === 'all') {
    return { engagement: 'audit', withRetainer };
  }
  return { engagement: 'build', withRetainer };
}

/*
 * buildPlan — assembles the personalised readout shown on the results
 * screen. Takes the raw answers plus whatever engagement the server
 * returned, and produces sentences that reference *this* respondent's
 * business rather than a generic template.
 */
export function buildPlan(answers, serverRec) {
  const a = answers || {};
  const rec = serverRec || recommendEngagement(a);
  const score = readinessScore(a);
  const b = band(score);

  const businessLabel = BUSINESS_LABEL[a.business] || BUSINESS_LABEL.other;
  const teamLabel = TEAM_LABEL[a.team] || 'your team';
  const systemKeys = SYSTEMS_FOR[a.bottleneck] || SYSTEMS_FOR.unsure;
  const systems = systemKeys.map((k) => SYSTEMS[k]);

  // One sentence that proves we read their answers.
  const situation =
    `You run ${businessLabel} with ${teamLabel}. ` +
    (a.bottleneck === 'unsure'
      ? 'You know something is being lost to manual work but not yet where.'
      : a.bottleneck === 'all'
        ? 'All three of support, operations, and pipeline are costing you time at once.'
        : `The time is going into ${
            a.bottleneck === 'support' ? 'answering the same questions repeatedly'
            : a.bottleneck === 'ops'   ? 'moving information between systems by hand'
            : 'finding and reaching the right prospects'
          }.`);

  // The honest first move, keyed off the engagement.
  const firstMove = {
    call:   'A 30-minute call. Nothing to prepare, nothing to sign.',
    audit:  `Map it before you build it. One to two days, free, and you keep the ranked plan either way — including the part that says which of these ${systems.length} systems is not worth building yet.`,
    build:  `Scope ${systems[0].name.toLowerCase()} on a call, price it in writing, then build. You already know where the pain is.`,
    rescue: 'Find out why the last one died before spending on the next one. Two days to a verdict.',
  }[rec.engagement];

  // Prior-attempt acknowledgement, only when there is one worth naming.
  const priorNote = {
    'agency-failed':
      'You have already paid an agency for something that never reached production. That is the single strongest predictor that the problem was never the model.',
    'freelancer-failed':
      'A freelancer built something that never shipped. Usually that means it worked in isolation and was never wired into anything real.',
    'freelancer-ok':
      'You have something partly working from a freelancer. Worth checking what is salvageable before rebuilding from zero.',
    chatgpt:
      'Doing it by hand in ChatGPT is the right way to find out whether it is worth automating. It is also the ceiling.',
    never: null,
  }[a.tried] || null;

  return {
    ...rec,
    score,
    bandKey: b.key,
    bandLabel: b.label,
    bandNote: b.note,
    situation,
    systems,
    firstMove,
    priorNote,
  };
}
